import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiRequest } from "../../helpers/api";
import { IntegrationCardsSkeleton } from "../../components/skeleton/PageSkeletons";
import { showError, showSuccess } from "../../components/ToastNotifications";
import { Can } from "../../components/AccessContext";

const providers = {
  instagram: { name: "Instagram", icon: "ti-brand-instagram", purpose: "Publish photos, videos, and Reels.", idLabel: "Meta App ID", secretLabel: "Meta App Secret", note: "Enable the Instagram API product and required business permissions in Meta." },
  facebook: { name: "Facebook Pages", icon: "ti-brand-facebook", purpose: "Publish and manage Facebook Page content.", idLabel: "Meta App ID", secretLabel: "Meta App Secret", note: "Uses the same Meta app as Instagram. Enable Facebook Login and Page publishing permissions." },
  linkedin: { name: "LinkedIn", icon: "ti-brand-linkedin", purpose: "Publish professional updates and media.", idLabel: "Client ID", secretLabel: "Client Secret", note: "Enable Sign In with LinkedIn and the approved Share on LinkedIn product." },
  x: { name: "X", icon: "ti-brand-x", purpose: "Publish text, images, and video.", idLabel: "OAuth 2.0 Client ID", secretLabel: "Client Secret", note: "Configure an OAuth 2.0 web app with PKCE and the registered callback URL." },
  youtube: { name: "YouTube", icon: "ti-brand-youtube", purpose: "Upload videos and access channel data.", idLabel: "Google OAuth Client ID", secretLabel: "Google Client Secret", note: "Enable YouTube Data API v3 and register the exact callback URL in Google Cloud." },
  tiktok: { name: "TikTok", icon: "ti-brand-tiktok", purpose: "Authorize video publishing and uploads.", idLabel: "Client Key", secretLabel: "Client Secret", note: "Enable Login Kit and the required video scopes in the TikTok developer portal." },
  pinterest: { name: "Pinterest", icon: "ti-brand-pinterest", purpose: "Create and manage Pins and boards.", idLabel: "App ID", secretLabel: "App Secret", note: "Request the user_accounts and pins scopes for your Pinterest app." },
  threads: { name: "Threads", icon: "ti-at", purpose: "Publish text and media posts.", idLabel: "Meta App ID", secretLabel: "Meta App Secret", note: "Enable Threads API and publishing permissions in the Meta developer console." },
  google_business: { name: "Google Business Profile", icon: "ti-brand-google-maps", purpose: "Publish business profile posts and manage locations.", idLabel: "Google OAuth Client ID", secretLabel: "Google Client Secret", note: "Enable Business Profile APIs and request business.manage access in Google Cloud." }
};

const statusLabel = (configured, error) => error ? "Error" : configured ? "Configured" : "Configuration Required";
const formatDate = (value) => value ? new Date(value).toLocaleString() : "";

const GoogleOAuthSettings = () => {
  const [platforms, setPlatforms] = useState([]);
  const [clientIds, setClientIds] = useState({});
  const [clientSecrets, setClientSecrets] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState("");
  const [error, setError] = useState("");
  const [actionErrors, setActionErrors] = useState({});

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await apiRequest("/api/admin/settings/social-oauth");
      const nextPlatforms = result.platforms || [];
      setPlatforms(nextPlatforms);
      setClientIds(Object.fromEntries(nextPlatforms.map((item) => [item.platform, item.clientId || ""])));
      setActionErrors({});
    } catch (requestError) {
      setError(requestError.message || "Unable to load integration settings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const socialEntries = useMemo(() => Object.entries(providers)
    .map(([key, provider]) => ({ id: key, ...provider, settings: platforms.find((item) => item.platform === key) || null })), [platforms]);
  const encryptionMissing = platforms.some((item) => !item.encryptionKeyConfigured);
  const jwtMissing = platforms.some((item) => !item.jwtConfigured);

  const saveProvider = async (event, platform) => {
    event.preventDefault();
    setSaving(platform);
    setActionErrors((current) => ({ ...current, [platform]: "" }));
    try {
      const result = await apiRequest(`/api/admin/settings/social-oauth/${platform}`, {
        method: "PUT",
        body: JSON.stringify({ clientId: clientIds[platform] || "", clientSecret: clientSecrets[platform] || "" })
      });
      setPlatforms((current) => current.map((item) => item.platform === platform ? { ...item, ...result } : item));
      setClientSecrets((current) => ({ ...current, [platform]: "" }));
      showSuccess(result.message || `${providers[platform].name} configuration saved securely.`);
    } catch (requestError) {
      const message = requestError.message || `Unable to save ${providers[platform].name} configuration.`;
      setActionErrors((current) => ({ ...current, [platform]: message }));
      showError(message);
    } finally {
      setSaving("");
    }
  };

  const renderSocialCard = ({ id, name, icon, purpose, idLabel, secretLabel, note, settings }) => {
    const status = settings || {};
    const actionError = actionErrors[id];
    const isSaving = saving === id;
    const statusText = statusLabel(status.configured, actionError);
    const statusClass = actionError ? "is-error" : status.configured ? "is-configured" : "is-required";
    const disabled = isSaving || !status.encryptionKeyConfigured;

    return <article className="admin-integration-card" key={id}>
      <header className="admin-integration-card-heading">
        <span className="admin-integration-icon"><i className={`ti ${icon}`} aria-hidden="true" /></span>
        <div className="admin-integration-card-title"><h3>{name}</h3><p>{purpose}</p></div>
        <span className={`admin-integration-status ${statusClass}`}><span aria-hidden="true" />{statusText}</span>
      </header>
      <div className="admin-integration-card-meta">
        <span>{status.source === "settings" ? "Encrypted admin configuration" : status.source === "environment" ? "API environment configuration" : "No credentials configured"}</span>
        {status.updatedAt && <time dateTime={status.updatedAt}>Updated {formatDate(status.updatedAt)}</time>}
      </div>
      {actionError && <p className="admin-integration-inline-error" role="alert">{actionError}</p>}
      <details className="admin-integration-config">
        <summary>{status.credentialsConfigured ? "Update configuration" : "Configure integration"}</summary>
        <form onSubmit={(event) => saveProvider(event, id)}>
          <label htmlFor={`${id}-client-id`}>{idLabel}
            <input id={`${id}-client-id`} type="text" autoComplete="off" maxLength={255} value={clientIds[id] || ""} onChange={(event) => setClientIds((current) => ({ ...current, [id]: event.target.value }))} required disabled={disabled} />
          </label>
          <label htmlFor={`${id}-client-secret`}>{secretLabel}
            <input id={`${id}-client-secret`} type="password" autoComplete="new-password" minLength={8} maxLength={512} value={clientSecrets[id] || ""} onChange={(event) => setClientSecrets((current) => ({ ...current, [id]: event.target.value }))} placeholder={status.secretConfigured ? "Configured — leave blank to keep current secret" : "Enter provider secret"} required={!status.secretConfigured} disabled={disabled} />
          </label>
          <p className="admin-integration-form-note">{note} Secret values are write-only and encrypted by the API.</p>
          {status.callbackUrl && <label htmlFor={`${id}-callback`}>Authorized callback URL
            <input id={`${id}-callback`} className="is-monospace" value={status.callbackUrl} readOnly onFocus={(event) => event.target.select()} />
          </label>}
          {status.secretConfigured && <p className="admin-integration-secret-hint"><i className="ti ti-lock" aria-hidden="true" /> A secret is configured. Its value is never sent to this page.</p>}
          <Can permission="admin.integrations.manage"><button className="admin-integration-button primary" type="submit" disabled={disabled || (!clientSecrets[id] && !status.secretConfigured)}>
            <i className={`ti ${isSaving ? "ti-loader-2" : "ti-device-floppy"}`} aria-hidden="true" />{isSaving ? "Saving securely…" : "Save configuration"}
          </button></Can>
          {!status.encryptionKeyConfigured && <p className="admin-integration-form-note is-warning">Saving is disabled until the API encryption key is configured.</p>}
        </form>
      </details>
    </article>;
  };

  return <section className="workspace-dashboard admin-integrations">
    {(encryptionMissing || jwtMissing) && !loading && <aside className="admin-integration-alert" role="alert">
      <i className="ti ti-alert-triangle" aria-hidden="true" />
      <div><strong>{encryptionMissing ? "Encryption key required" : "OAuth configuration required"}</strong>
        <p>{encryptionMissing
          ? "Set SOCIAL_TOKEN_ENCRYPTION_KEY in the API environment to canonical base64 for exactly 32 cryptographically random bytes, then restart the API. Do not replace an existing key; encrypted credentials depend on it. Keep this server-only value out of frontend environment variables."
          : "Set JWT_SECRET in the API environment and restart the API before enabling OAuth sign-in."}</p>
      </div>
    </aside>}

    {error && <div className="admin-integration-error" role="alert">
      <div><strong>Integration settings could not be loaded</strong><p>{error} If the API did not start, check its server-side configuration logs and confirm the existing encryption key is present. Do not replace a key for an installation with encrypted data.</p></div>
      <button className="admin-integration-button" type="button" onClick={loadSettings} disabled={loading}>Retry</button>
    </div>}

    {loading && <IntegrationCardsSkeleton />}
    {!loading && !error && <>
      <section className="admin-integration-section">
        <header>
          <div>
            <h2>Social platform integrations</h2>
            <p>OAuth app credentials used to authorize social accounts. This does not disconnect existing accounts.</p>
          </div>
          <button className="admin-integration-button" type="button" onClick={loadSettings} disabled={loading}>
            <i className={`ti ${loading ? "ti-loader-2" : "ti-refresh"}`} aria-hidden="true" /> Refresh status
          </button>
        </header>
        <div className="admin-integration-grid">{socialEntries.map(renderSocialCard)}</div>
      </section>
    </>}
  </section>;
};

export default GoogleOAuthSettings;
